import { type ComponentPropsWithRef, memo } from 'react';

import { Check, Close } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { cn } from '@air/utils-tailwind';

import { getContrastColor } from '~/components/v2/utils/filters/getContrastColor';

export type CustomColorValueProps = ComponentPropsWithRef<'div'> & {
  hex: string;
  isSelected?: boolean;
  onSelect: () => void;
  onRemoveHex: () => void;
};

export const CustomColorValue = memo(
  ({ className, hex, isSelected, onSelect, onRemoveHex, ...restOfProps }: CustomColorValueProps) => {
    return (
      <div
        className={cn(
          'relative flex h-6 items-center gap-1 rounded-24 border border-grey-5 px-1 uppercase ring-0 transition-colors hover:bg-grey-4 data-[selected=true]:border-grey-10 data-[selected=true]:ring-1 data-[selected=true]:ring-grey-10 data-[selected=true]:hover:bg-grey-4',
          className,
        )}
        data-selected={isSelected}
        data-hex={hex}
        data-testid="CUSTOM_COLOR_CHIP"
        {...restOfProps}
      >
        <button className="absolute inset-0 size-full" onClick={onSelect} type="button">
          <VisuallyHidden>Select {hex}</VisuallyHidden>
        </button>
        <div className="flex size-4 items-center justify-center rounded-full" style={{ backgroundColor: hex }}>
          {isSelected && <Check className="size-3" style={{ color: getContrastColor(hex) }} />}
        </div>
        <span className="font-medium text-12 text-grey-12">{hex}</span>
        <IconButton
          appearance="ghost"
          color="grey"
          data-hex={hex}
          data-testid="CUSTOM_COLOR_REMOVE_BUTTON"
          icon={Close}
          label={`Remove ${hex}`}
          onClick={onRemoveHex}
          size="extra-small"
        />
      </div>
    );
  },
);

CustomColorValue.displayName = 'CustomColorValue';
